import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, StatusBadge, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Building2, MapPin, Plus } from "lucide-react";
import { toast } from "sonner";

const emptyP = { name: "", city: "", address: "", phone: "", email: "", vat: "", status: "active" };
const emptyCp = { name: "", address: "", phone: "", partner_id: "", status: "active" };

export default function NetworkPage() {
  const [tab, setTab] = useState("partner");
  const [partners, setPartners] = useState(null);
  const [cps, setCps] = useState(null);
  const [pForm, setPForm] = useState(emptyP);
  const [pOpen, setPOpen] = useState(false);
  const [cpForm, setCpForm] = useState(emptyCp);
  const [cpOpen, setCpOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/partners").then((r) => setPartners(r.data));
    api.get("/cambio-points").then((r) => setCps(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveP = async () => {
    try { await api.post("/partners", pForm); toast.success("Partner creato"); setPOpen(false); setPForm(emptyP); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveCp = async () => {
    try { await api.post("/cambio-points", cpForm); toast.success("Cambio Point creato"); setCpOpen(false); setCpForm(emptyCp); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const partnerName = (id) => partners?.find((p) => p.id === id)?.name || "-";

  return (
    <div>
      <PageHeader title="Network" subtitle="Partner e Cambio Point del network" />
      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList><TabsTrigger value="partner" data-testid="tab-partner">Partner</TabsTrigger><TabsTrigger value="cp" data-testid="tab-cp">Cambio Point</TabsTrigger></TabsList>
      </Tabs>

      {tab === "partner" && (!partners ? <Loading /> : (
        <div>
          <div className="flex justify-end mb-3">
            <Dialog open={pOpen} onOpenChange={setPOpen}>
              <DialogTrigger asChild><Button className="bg-[#0B2447]" data-testid="add-partner-btn"><Plus size={16} className="mr-1" />Nuovo Partner</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nuovo Partner</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Nome lavanderia</Label><Input data-testid="partner-name" className="mt-1" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Città</Label><Input className="mt-1" value={pForm.city} onChange={(e) => setPForm({ ...pForm, city: e.target.value })} /></div>
                    <div><Label>Telefono</Label><Input className="mt-1" value={pForm.phone} onChange={(e) => setPForm({ ...pForm, phone: e.target.value })} /></div>
                  </div>
                  <div><Label>Indirizzo</Label><Input className="mt-1" value={pForm.address} onChange={(e) => setPForm({ ...pForm, address: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Email</Label><Input className="mt-1" value={pForm.email} onChange={(e) => setPForm({ ...pForm, email: e.target.value })} /></div>
                    <div><Label>P.IVA</Label><Input className="mt-1" value={pForm.vat} onChange={(e) => setPForm({ ...pForm, vat: e.target.value })} /></div>
                  </div>
                </div>
                <DialogFooter><Button onClick={saveP} data-testid="save-partner" className="bg-[#0B2447]">Salva</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {partners.map((p) => (
              <Card key={p.id} className="p-5" data-testid={`partner-${p.code}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3"><span className="p-2.5 rounded-xl bg-[#E4F5FB] text-[#0EA5C4]"><Building2 size={20} /></span>
                    <div><p className="font-display font-bold text-[#0B2447]">{p.name}</p><p className="text-xs text-slate-400">{p.code} · {p.city}</p></div>
                  </div>
                  <StatusBadge status={p.status} />
                </div>
                <p className="text-sm text-slate-500 mt-3">{p.address}</p>
                <p className="text-xs text-slate-400">{p.phone} · {p.email}</p>
              </Card>
            ))}
          </div>
        </div>
      ))}

      {tab === "cp" && (!cps ? <Loading /> : (
        <div>
          <div className="flex justify-end mb-3">
            <Dialog open={cpOpen} onOpenChange={setCpOpen}>
              <DialogTrigger asChild><Button className="bg-[#0B2447]" data-testid="add-cp-btn"><Plus size={16} className="mr-1" />Nuovo Cambio Point</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nuovo Cambio Point</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Partner</Label>
                    <Select value={cpForm.partner_id} onValueChange={(v) => setCpForm({ ...cpForm, partner_id: v })}>
                      <SelectTrigger className="mt-1" data-testid="cp-partner"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>{partners?.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Nome</Label><Input data-testid="cp-name" className="mt-1" value={cpForm.name} onChange={(e) => setCpForm({ ...cpForm, name: e.target.value })} /></div>
                  <div><Label>Indirizzo</Label><Input className="mt-1" value={cpForm.address} onChange={(e) => setCpForm({ ...cpForm, address: e.target.value })} /></div>
                  <div><Label>Telefono</Label><Input className="mt-1" value={cpForm.phone} onChange={(e) => setCpForm({ ...cpForm, phone: e.target.value })} /></div>
                </div>
                <DialogFooter><Button onClick={saveCp} data-testid="save-cp" className="bg-[#0B2447]">Salva</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cps.map((cp) => (
              <Card key={cp.id} className="p-5" data-testid={`cp-${cp.code}`}>
                <div className="flex items-center gap-3"><span className="p-2.5 rounded-xl bg-[#E4F5FB] text-[#0EA5C4]"><MapPin size={20} /></span>
                  <div><p className="font-display font-bold text-[#0B2447]">{cp.name}</p><p className="text-xs text-slate-400">{cp.code} · {partnerName(cp.partner_id)}</p></div>
                </div>
                <p className="text-sm text-slate-500 mt-3">{cp.address}</p>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
